/**
 * Single source of truth for Bastion webapp product navigation:
 * top-level product areas, their child pages, and pathname → product matching.
 *
 * The icon rail + secondary sidebar consume this list. Chat/home is reached from
 * `/` and `/chat/*`; `matchProductGroup` still returns `"chat"` for those URLs.
 */

import type { ComponentType } from "react";
import {
  Home,
  Film,
  Database,
  Server,
  Activity,
  Shield,
  Users,
  LayoutDashboard,
  BrainCircuit,
  Bot,
  ClipboardCheck,
  Palette,
  Layers,
  ListChecks,
  MapPin,
} from "lucide-react";

/** Product areas shown in the icon rail + secondary nav. */
export type ProductTabId = "home" | "data-studio" | "ml-studio" | "analytics" | "fleet";

export type ProductGroupId = ProductTabId | "chat" | "settings";

type IconComponent = ComponentType<{ className?: string }>;

export type ProductTopTab = {
  id: ProductTabId;
  label: string;
  /** Icon on the far-left rail. */
  icon: IconComponent;
  /** Default route when the user clicks the rail icon. */
  defaultPath: string;
  items: readonly ProductNavItem[];
};

export type ProductNavItem = {
  id: string;
  label: string;
  description: string;
  path: string;
  icon: IconComponent;
  internalOnly?: boolean;
};

export const PRODUCT_TOP_TABS: readonly ProductTopTab[] = [
  {
    id: "home",
    label: "Home",
    icon: Home,
    defaultPath: "/",
    items: [
      {
        id: "home-root",
        label: "Home",
        description: "Assistant home and conversations",
        path: "/",
        icon: Home,
      },
    ],
  },
  {
    id: "data-studio",
    label: "Data Studio",
    icon: Database,
    defaultPath: "/episodes",
    items: [
      {
        id: "/episodes",
        label: "Episodes",
        description: "Browse, review, and filter collected robot runs",
        path: "/episodes",
        icon: Film,
      },
      {
        id: "/review",
        label: "Review",
        description: "Review your uploaded teleop episodes oldest-first",
        path: "/review",
        icon: ClipboardCheck,
      },
      {
        id: "/subtasks",
        label: "Subtasks",
        description: "Browse labeled subtask segments across all episodes",
        path: "/subtasks",
        icon: Layers,
      },
      {
        id: "/datasets",
        label: "Datasets",
        description: "Curate training datasets and inspect dataset contents",
        path: "/datasets",
        icon: Database,
      },
      {
        id: "/sops",
        label: "SOPs",
        description:
          "Teleop, Review, and Eval SOPs in one place — versioned rubrics that drive collection, review sign-off, and evaluation",
        path: "/sops",
        icon: Shield,
      },
      {
        id: "/jobs",
        label: "Jobs",
        description: "Monitor thumbnails and video review copies",
        path: "/jobs",
        icon: ListChecks,
        internalOnly: true,
      },
    ],
  },
  {
    id: "ml-studio",
    label: "ML Studio",
    icon: BrainCircuit,
    defaultPath: "/ml-studio/workflows",
    items: [
      {
        id: "/ml-studio/workflows",
        label: "Workflows",
        description: "Launch and monitor training workflow runs",
        path: "/ml-studio/workflows",
        icon: Activity,
      },
      {
        id: "/ml-studio/artifacts",
        label: "Artifacts",
        description: "Inspect generated models, logs, and run outputs",
        path: "/ml-studio/artifacts",
        icon: Database,
      },
      {
        id: "/ml-studio/policies",
        label: "Policies",
        description: "Compare trained policies and deployment candidates",
        path: "/ml-studio/policies",
        icon: Shield,
      },
      {
        id: "/ml-studio/policy-evals",
        label: "Policy evals",
        description: "Compare evaluated policy checkpoints by SOP and eval version",
        path: "/ml-studio/policy-evals",
        icon: ClipboardCheck,
      },
      {
        id: "/ml-studio/metrics",
        label: "Metrics",
        description: "Track training curves and policy performance",
        path: "/ml-studio/metrics",
        icon: Activity,
      },
      {
        id: "/devservers",
        label: "Devservers",
        description: "Provision and manage remote development machines",
        path: "/devservers",
        icon: Server,
      },
    ],
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: Activity,
    defaultPath: "/dashboards",
    items: [
      {
        id: "/dashboards",
        label: "Dashboards",
        description: "Build and view operational dashboards",
        path: "/dashboards",
        icon: LayoutDashboard,
      },
      {
        id: "/collector-insights",
        label: "Collectors",
        description: "Understand collector throughput and quality signals",
        path: "/collector-insights",
        icon: Users,
      },
      {
        id: "/design",
        label: "Design system",
        description: "Review shared UI primitives and design tokens",
        path: "/design",
        icon: Palette,
      },
    ],
  },
  {
    id: "fleet",
    label: "Fleet",
    icon: Bot,
    defaultPath: "/fleet",
    items: [
      {
        id: "/fleet",
        label: "Sites",
        description: "Robot health and open incidents at each site",
        path: "/fleet",
        icon: MapPin,
      },
      {
        id: "/fleet/robots",
        label: "Robots",
        description: "Discover machines on the tailnet and open a robot's live console",
        path: "/fleet/robots",
        icon: Bot,
      },
      {
        id: "/telemetry/sessions",
        label: "Sessions",
        description: "Browse telemetry sessions uploaded from robots",
        path: "/telemetry/sessions",
        icon: Activity,
      },
    ],
  },
] as const;

const SETTINGS_DEFAULT_PATH = "/settings";

/**
 * Products that only the Parametric organization holds. Every route behind
 * them carries the internal-only posture, so an external organization gets 403
 * from the data. Leaving them out of the rail keeps the shell honest about
 * what the user can open.
 */
const PARAMETRIC_ONLY_TAB_IDS: readonly ProductTabId[] = ["home", "ml-studio", "analytics"];

/**
 * The part of `GET /me` that selects what the shell renders. `null` and
 * `undefined` mean the answer has not arrived, or the request failed.
 */
export type OrgRendering = { is_parametric: boolean } | null | undefined;

/**
 * The product tabs to render for one organization.
 *
 * Default open: only an answered `is_parametric === false` removes a tab. A
 * loading or failed `/me` renders every tab, so an internal user never watches
 * the rail fill in. The accepted risk in `docs/bastion_authorization.md` is a
 * stale tab an external user cannot use, not a rail that flashes. The server
 * denies the request either way.
 */
export function visibleProductTabs(me: OrgRendering): readonly ProductTopTab[] {
  if (me?.is_parametric === false) {
    return PRODUCT_TOP_TABS.filter((tab) => !PARAMETRIC_ONLY_TAB_IDS.includes(tab.id))
      .map((tab) => ({ ...tab, items: tab.items.filter((item) => !item.internalOnly) }));
  }
  return PRODUCT_TOP_TABS;
}

/** True when `visibleProductTabs` keeps one tab for this organization. */
export function isProductTabVisible(id: ProductTabId, me: OrgRendering): boolean {
  return visibleProductTabs(me).some((tab) => tab.id === id);
}

/**
 * Where `/` lands. `/` renders the assistant workspace, which is Home, so an
 * organization without Home lands on the episode list instead.
 */
export function homeLandingPath(me: OrgRendering): string {
  return isProductTabVisible("home", me) ? "/" : "/episodes";
}

/**
 * Whether the router registers one route group for this organization.
 *
 * The route groups carry the product tab ids, so a tab the rail hides also
 * stops answering deep links: a hidden path falls through to the catch-all
 * and lands on `homeLandingPath`. Settings stays for every organization.
 * The same default-open rule applies: only an answered
 * `is_parametric === false` hides a group.
 */
export function isRouteGroupVisible(id: string, me: OrgRendering): boolean {
  const tab = PRODUCT_TOP_TABS.find((candidate) => candidate.id === id);
  return tab === undefined || isProductTabVisible(tab.id, me);
}

/** Map the current pathname to a product group. */
export function matchProductGroup(pathname: string): ProductGroupId {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;

  if (path === "/settings" || path.startsWith("/settings/")) {
    return "settings";
  }
  if (path === "/" || path.startsWith("/chat")) {
    return "chat";
  }
  if (
    path.startsWith("/episodes") ||
    path.startsWith("/subtasks") ||
    path.startsWith("/datasets") ||
    path.startsWith("/jobs") ||
    path.startsWith("/sops") ||
    // Legacy /review/sops paths redirect to /sops?tab=review; matching
    // them here keeps the rail tab highlighted during the brief flash
    // of the redirect.
    path.startsWith("/review")
  ) {
    return "data-studio";
  }
  if (
    path.startsWith("/collector-insights") ||
    path.startsWith("/dashboards") ||
    path.startsWith("/design")
  ) {
    return "analytics";
  }
  if (path.startsWith("/ml-studio") || path.startsWith("/devservers")) {
    return "ml-studio";
  }
  if (path.startsWith("/telemetry") || path.startsWith("/fleet")) {
    return "fleet";
  }
  return "chat";
}

export function defaultPathForProductGroup(group: ProductGroupId): string {
  if (group === "settings") return SETTINGS_DEFAULT_PATH;
  const tab = PRODUCT_TOP_TABS.find((t) => t.id === group);
  return tab?.defaultPath ?? "/";
}

export function getProductTab(group: ProductGroupId): ProductTopTab | null {
  if (group === "settings") return null;
  return PRODUCT_TOP_TABS.find((t) => t.id === group) ?? null;
}

/** True if the page at `itemPath` is currently active for `pathname`. */
export function isItemActive(pathname: string, itemPath: string): boolean {
  if (itemPath === "/") {
    return pathname === "/" || pathname.startsWith("/chat");
  }
  // The Sites page is `/fleet`, and the Robots page `/fleet/robots` is
  // under it. Sites is active for the overview and the site dashboards
  // only, so the two items are never active together.
  if (itemPath === "/fleet") {
    return pathname === "/fleet" || pathname.startsWith("/fleet/sites/");
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}
